import {
  Box,
  Button,
  Chip,
  Drawer,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import PageHeader from 'components/AppBar/PageHeader'
import { BasePathProvider } from 'components/Providers'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import {
  getBadgeColor,
  search,
} from 'store/visitor-log'
import VisitorLogList from './List'
import VisitorLogFormIndex from './Form'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
const baseUrl = '/visitor-log'

export default function VisitorLogPage() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams()

  const [tabIndex, setTabIndex] = useState(0)

  const visitorLogs = useSelector(
    state => state.visitorLog.list,
  )

  const badgeColor = useSelector(
    state => state.visitorLog.badgeColor,
  )

  const currentDate = dayjs().format('YYYY-MM-DD')

  useEffect(() => {
    if (!visitorLogs) {
      dispatch(
        search({
          date: currentDate,
        }),
      )
    }

    if (!badgeColor) {
      dispatch(getBadgeColor())
    }
  }, [
    dispatch,
    visitorLogs,
    badgeColor,
    currentDate,
  ])

  const currentlyInBuilding = Array.isArray(visitorLogs)
    ? visitorLogs.filter(
      item =>
        item.visitType !== 'pickup' &&
        !item.checkOutAt,
    )
    : []

  const checkedOut = Array.isArray(visitorLogs)
    ? visitorLogs.filter(
      item =>
        item.visitType !== 'pickup' &&
        Boolean(item.checkOutAt),
    )
    : []

  const studentPickups = Array.isArray(visitorLogs)
    ? visitorLogs.filter(item => item.visitType === 'pickup')
    : []

  const tabLists = [
    currentlyInBuilding,
    checkedOut,
    studentPickups,
  ]

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue)
  }

  return (
    <BasePathProvider value={baseUrl}>
      <Box sx={{ mb: 2 }}>
        <PageHeader title={t('Visitor Management')} />
      </Box>

      <Paper sx={{ width: '100%', p: 2, mb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 400 }}
            >
              Visitor Log - {dayjs().format('dddd, MMMM D, YYYY')}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
            >
              {t('Track all visitors, check-ins, and student pickups')}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Chip
              icon={
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    backgroundColor: badgeColor?.color || 'grey',
                  }}
                />
              }
              label={`${t("Today's Pass Color")}: ${badgeColor?.color || '-'}`}
              variant="outlined"
              sx={{
                textTransform: 'capitalize',
              }}
            />

            <Button
              variant="contained"
              onClick={() => navigate(`${baseUrl}/new`)}
            >
              + {t('New Check-In')}
            </Button>
          </Box>
        </Box>

        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          sx={{ mt: 2 }}
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', gap: 1 }}>
                {t('Currently in Building')}
                <Chip
                  size="small"
                  label={currentlyInBuilding.length}
                />
              </Box>
            }
          />

          <Tab
            label={
              <Box sx={{ display: 'flex', gap: 1 }}>
                {t('Checked Out')}
                <Chip
                  size="small"
                  label={checkedOut.length}
                />
              </Box>
            }
          />

          <Tab
            label={
              <Box sx={{ display: 'flex', gap: 1 }}>
                {t('Student Pickups')}
                <Chip
                  size="small"
                  label={studentPickups.length}
                />
              </Box>
            }
          />
        </Tabs>
      </Paper>

      <Paper
        sx={{
          width: '100%',
          minHeight: 500,
          p: 2,
          borderRadius: 2,
        }}
      >
        <VisitorLogList
          list={tabLists[tabIndex]}
          tabIndex={tabIndex}
        />
      </Paper>

      <Drawer
        PaperProps={{
          sx: {
            width: 500,
          },
        }}
        open={Boolean(id)}
        anchor="right"
        onClose={() => navigate(baseUrl)}
      >
        <VisitorLogFormIndex />
      </Drawer>
    </BasePathProvider>
  )
}